// Shared corner-claim (voice) resolution logic — DESIGN.md §11/§16, round-16/20/22. Split out
// of NewMatch.vue (round-23, 2026-08-31 fix: "I play on the blue side" said on Home was heard
// and classified fine, per the debug panel, but did nothing — corner_claim was only ever wired
// up in NewMatch.vue's own onSttCommand subscription, so the claim fired into a void on every
// other screen). Both NewMatch.vue (its own popup + card glow) and App.vue's central
// onSttCommand hub (Home's new claim -> pre-assign -> jump-to-New-match flow, plus the toast
// fallback everywhere else) now call the SAME resolution here, so the speaker-matching logic and
// its "track everything" (DESIGN.md §16) outcomes exist in exactly one place.
//
// Pure resolution only — no UI, no navigation, no draft mutation. The caller decides what to do
// with the result: NewMatch.vue still does setSide + flashGlow + its own (unchanged, English)
// popup text; App.vue's hub does setSide + navigate + an es-ES toast (DESIGN.md item 2, 2026-08-31).
//
// Round-27 (2026-08-31): a THIRD outcome, 'needs_confirmation' (voiceScore.ts's classifyConfidenceBand
// 'dubious' band) — a clear winner that doesn't clear the auto-accept bar. Still pure resolution
// here (fetches the candidate player so the confirm prompt has a name/avatar ready, tracks the
// attempt, records it on the transcript) but applies NOTHING — every caller reacts by handing the
// result to services/confirmFlow.ts's startConfirmation(), which owns the ask/answer state
// machine and the eventual setSide on a 'yes'.
import { getPlayer, getVoiceProfiles, postVoiceConfirmationInteraction } from '../backend'
import { draft } from '../store/draft'
import { spkForCommand, recordSpeakerMatchForCommand, type SttCommandEvent } from '../stt'
import { rankSpeakerCandidates, classifyConfidenceBand } from '../voiceScore'
import { allScope, pickedScope, scopedCandidates, scopeLabel, type CandidateScope } from './candidateScope'
import { shipEvent } from '../logging'
import type { Player, Side, VoiceProfile } from '../types'

export type CornerClaimOutcome =
  | 'no_fingerprint'
  | 'below_confidence_threshold'
  | 'rejected_duplicate_side'
  | 'accepted'
  | 'player_load_failed'
  // Round-27 (2026-08-31): the new "dubious" confidence band (voiceScore.ts's
  // classifyConfidenceBand) — real signal, clear winner over any runner-up, but short of the
  // auto-accept bar. The caller (App.vue's hub / NewMatch.vue) starts a confirmation flow
  // (services/confirmFlow.ts) instead of assigning OR silently dropping.
  | 'needs_confirmation'

export interface CornerClaimResult {
  outcome: CornerClaimOutcome
  side: Side
  color: 'red' | 'blue'
  /** Set on 'accepted' AND 'needs_confirmation' — the caller applies it (setSide) immediately
   *  for 'accepted', or shows it in the confirm prompt (name/avatar) and applies it only once the
   *  answer comes back 'yes' for 'needs_confirmation'. */
  player: Player | null
  /** Set only on 'rejected_duplicate_side' — who's already on the OTHER side. */
  other: Player | null
  confidencePct: number | null
  /** English text, verbatim what NewMatch.vue's popup showed pre-refactor — kept as-is so that
   *  screen's UI text doesn't change under this split. Other callers (Home's toast) compose
   *  their own copy from `outcome`/`player`/`other` instead of reusing this string. */
  message: string
  /** Set only on 'needs_confirmation' — everything services/confirmFlow.ts needs to run the
   *  ask/answer flow and persist a complete observability row (raw cosine, runner-up + margin,
   *  utterance shape) without re-deriving any of it. */
  confirm: {
    candidateId: number
    rawCosine: number
    runnerUp: { id: number; cosine: number; confidencePct: number } | null
    marginPct: number
    durationSec: number | null
  } | null
  /** Round-28 (2026-08-31) — the candidate-scope label actually applied (services/
   *  candidateScope.ts's scopeLabel), e.g. "picked players (1)" or "all enrolled (4)". Exposed so
   *  confirmFlow.ts's observability row and DebugStt's transcript display don't have to
   *  re-derive it from the caller's own context. */
  scopeLabel: string
}

let voiceProfiles: VoiceProfile[] | null = null
async function loadVoiceProfiles(): Promise<VoiceProfile[]> {
  if (voiceProfiles) return voiceProfiles
  try {
    voiceProfiles = (await getVoiceProfiles()).items.filter((pr) => pr.centroid && pr.centroid.length > 0)
  } catch {
    voiceProfiles = []
  }
  return voiceProfiles
}

// Round-22: "track everything" (DESIGN.md § Observability) — every corner-claim attempt ships to
// Loki, accepted or rejected, so debugging a bad/good match works from the eventstream. Compact
// fields only — ids/scores/text, never the x-vector or audio.
function trackCornerClaim(cmd: SttCommandEvent, outcome: string, extra: Record<string, unknown> = {}) {
  shipEvent('corner-claim', { raw: cmd.raw, lang: cmd.lang, color: cmd.color, outcome, ...extra })
}

// Round-27: best-effort observability POST — never awaited by the caller, never lets a network
// hiccup affect the corner-claim UX (same discipline as trackCornerClaim's shipEvent above, just
// a durable DB row instead of a Loki line). Exported so confirmFlow.ts can post the SAME shape
// once an ask/answer interaction resolves (confirmed/denied/timeout/cancelled) — this function
// only ever fires the 'auto' case (immediate high-band accept, no ask).
export function postConfirmationInteractionBestEffort(payload: Parameters<typeof postVoiceConfirmationInteraction>[0]) {
  postVoiceConfirmationInteraction(payload).catch(() => {
    /* best-effort — never surfaces to the UI */
  })
}

// Round-28 (2026-08-31): which candidate-scope tier applies to a corner claim is entirely
// determined by which screen is asking — corner_claim only ever fires from 'home' (nobody's
// picked yet, could be anyone who just walked up: 'all') or 'newMatch' (narrow to whoever's
// already been picked for a side, if anyone — see candidateScope.ts's 'picked' tier docblock —
// else 'all', same as home). Derived internally rather than threaded as a new parameter: the
// screen->scope mapping is closed and small, and every existing call site already passes
// `screen`, so no call site needs to change for this.
function scopeForCornerClaimScreen(screen: string | undefined): CandidateScope {
  if (screen === 'newMatch') {
    const pickedIds = [draft.left?.id, draft.right?.id].filter((id): id is number => id != null)
    return pickedScope(pickedIds)
  }
  return allScope()
}

export async function resolveCornerClaim(cmd: SttCommandEvent, screen?: string): Promise<CornerClaimResult | null> {
  if (!cmd.color) return null
  const side: Side = cmd.color === 'red' ? 'left' : 'right'
  const other = side === 'left' ? draft.right : draft.left
  const scope = scopeForCornerClaimScreen(screen)
  const fp = spkForCommand(cmd)
  if (!fp) {
    trackCornerClaim(cmd, 'no_fingerprint')
    return {
      outcome: 'no_fingerprint',
      side,
      color: cmd.color,
      player: null,
      other: null,
      confidencePct: null,
      message: `Heard "${cmd.raw}" — no voice fingerprint to match (train your voice first)`,
      confirm: null,
      scopeLabel: scopeLabel(scope, 0),
    }
  }
  const profiles = await loadVoiceProfiles()
  const candidates = scopedCandidates(profiles, scope)
  const scopeStr = scopeLabel(scope, candidates.length)
  const ranked = rankSpeakerCandidates(fp.spk, fp.spkModelVersion, candidates)
  // Round-27: classifyConfidenceBand replaces the old binary bestSpeakerConfidence() gate with
  // three bands (voiceScore.ts docblock) — 'high' keeps the exact pre-existing auto-accept
  // behavior below, 'low' keeps the exact pre-existing below_confidence_threshold behavior,
  // 'dubious' is new (needs_confirmation, see services/confirmFlow.ts).
  const bandResult = classifyConfidenceBand(ranked, fp.durationSec)
  const match = bandResult.top
  const candidateSummary = ranked.map((r) => ({ id: r.id, cos: Math.round(r.similarity * 1000) / 1000 }))
  if (!match || bandResult.band === 'low') {
    trackCornerClaim(cmd, 'below_confidence_threshold', {
      durationSec: fp.durationSec,
      candidates: candidateSummary,
      scope: scopeStr,
      // Present even when a candidate existed but didn't clear the dubious band's floor/margin
      // gate — makes "why didn't it even ask" debuggable from the eventstream.
      topConfidence: match ? Math.round(match.confidence * 1000) / 1000 : null,
      margin: bandResult.margin != null ? Math.round(bandResult.margin * 1000) / 1000 : null,
    })
    return {
      outcome: 'below_confidence_threshold',
      side,
      color: cmd.color,
      player: null,
      other: null,
      confidencePct: null,
      message: `Heard "${cmd.raw}" — voice not recognized, pick a name manually`,
      confirm: null,
      scopeLabel: scopeStr,
    }
  }
  if (other && other.id === match.id) {
    trackCornerClaim(cmd, 'rejected_duplicate_side', {
      durationSec: fp.durationSec,
      candidates: candidateSummary,
      chosenId: match.id,
      cos: Math.round(match.similarity * 1000) / 1000,
      confidence: Math.round(match.confidence * 1000) / 1000,
      band: bandResult.band,
      scope: scopeStr,
    })
    recordSpeakerMatchForCommand(cmd, {
      playerId: match.id,
      displayName: other.displayName,
      confidencePct: Math.round(match.confidence * 100),
      outcome: 'rejected_duplicate_side',
      scope: scopeStr,
    })
    return {
      outcome: 'rejected_duplicate_side',
      side,
      color: cmd.color,
      player: null,
      other,
      confidencePct: Math.round(match.confidence * 100),
      message: `${other.displayName} is already on the other side`,
      confirm: null,
      scopeLabel: scopeStr,
    }
  }
  try {
    const player = await getPlayer(match.id)
    if (bandResult.band === 'dubious') {
      trackCornerClaim(cmd, 'needs_confirmation', {
        durationSec: fp.durationSec,
        candidates: candidateSummary,
        chosenId: match.id,
        cos: Math.round(match.similarity * 1000) / 1000,
        confidence: Math.round(match.confidence * 1000) / 1000,
        runnerUpId: bandResult.runnerUp?.id ?? null,
        margin: bandResult.margin != null ? Math.round(bandResult.margin * 1000) / 1000 : null,
        scope: scopeStr,
      })
      recordSpeakerMatchForCommand(cmd, {
        playerId: match.id,
        displayName: player.displayName,
        confidencePct: Math.round(match.confidence * 100),
        outcome: 'needs_confirmation',
        scope: scopeStr,
      })
      return {
        outcome: 'needs_confirmation',
        side,
        color: cmd.color,
        player,
        other: null,
        confidencePct: Math.round(match.confidence * 100),
        message: `Heard "${cmd.raw}" — ${player.displayName}? (${Math.round(match.confidence * 100)}%, asking to confirm)`,
        confirm: {
          candidateId: match.id,
          rawCosine: Math.round(match.similarity * 1000) / 1000,
          runnerUp: bandResult.runnerUp
            ? {
                id: bandResult.runnerUp.id,
                cosine: Math.round(bandResult.runnerUp.similarity * 1000) / 1000,
                confidencePct: Math.round(bandResult.runnerUp.confidence * 100),
              }
            : null,
          marginPct: Math.round((bandResult.margin ?? 0) * 100),
          durationSec: fp.durationSec ?? null,
        },
        scopeLabel: scopeStr,
      }
    }
    trackCornerClaim(cmd, 'accepted', {
      durationSec: fp.durationSec,
      candidates: candidateSummary,
      chosenId: match.id,
      cos: Math.round(match.similarity * 1000) / 1000,
      confidence: Math.round(match.confidence * 1000) / 1000,
      scope: scopeStr,
    })
    recordSpeakerMatchForCommand(cmd, {
      playerId: match.id,
      displayName: player.displayName,
      confidencePct: Math.round(match.confidence * 100),
      outcome: 'accepted',
      scope: scopeStr,
    })
    // Round-27: the 'auto' row for the observability table — the high band never touches
    // confirmFlow.ts (no ask), so this is the ONLY place an auto-accept's interaction gets
    // persisted. Without it "how often are we wrong" would have no denominator (only asks would
    // ever show up).
    postConfirmationInteractionBestEffort({
      candidatePlayerId: match.id,
      side,
      color: cmd.color,
      rawCosine: match.similarity,
      calibratedConfidence: match.confidence,
      runnerUpPlayerId: bandResult.runnerUp?.id ?? null,
      runnerUpCosine: bandResult.runnerUp?.similarity ?? null,
      margin: bandResult.margin,
      utteranceText: cmd.raw,
      utteranceLang: cmd.lang,
      utteranceDurationSec: fp.durationSec ?? null,
      resolution: 'auto',
      answerSource: null,
      latencyMs: null,
      screen: screen ?? null,
      ttsAttempted: false,
      ttsOk: null,
      scope: scopeStr,
      command: 'corner_claim',
    })
    return {
      outcome: 'accepted',
      side,
      color: cmd.color,
      player,
      other: null,
      confidencePct: Math.round(match.confidence * 100),
      message: `${player.displayName} → ${cmd.color === 'red' ? 'red' : 'blue'} corner (voice match ${Math.round(match.confidence * 100)}%)`,
      confirm: null,
      scopeLabel: scopeStr,
    }
  } catch {
    trackCornerClaim(cmd, 'player_load_failed', { chosenId: match.id, scope: scopeStr })
    recordSpeakerMatchForCommand(cmd, {
      playerId: match.id,
      displayName: null,
      confidencePct: Math.round(match.confidence * 100),
      outcome: 'player_load_failed',
      scope: scopeStr,
    })
    return {
      outcome: 'player_load_failed',
      side,
      color: cmd.color,
      player: null,
      other: null,
      confidencePct: null,
      message: 'Voice matched a player but could not load their profile',
      confirm: null,
      scopeLabel: scopeStr,
    }
  }
}
